import { calgaryDateString } from '../../../../../../utils/calgaryTime';
import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faPlus } from "@fortawesome/free-solid-svg-icons";
import { useQuery } from "@tanstack/react-query";
import FinancialService from "../../../../../../AppSettings/services/FinancialService";
import MatterService from "../../../../../../AppSettings/services/MatterService";
import EmployeeService from "../../../../../../AppSettings/services/EmployeeService";
import useActiveEmployeeId from "../../../../../../AppSettings/hooks/useActiveEmployeeId";
import useCurrentEmployee from "../../../../../../AppSettings/hooks/useCurrentEmployee";
import FormEntryMultilineTextbox from "@frontend/Components/Forms/Entry/MultilineTextbox/FormEntryMultilineTextbox";
import FormModal from "@frontend/Components/Modals/FormModal";
import { isFeatureEnabled } from "@frontend/AppSettings/environmentConfig";
import { useDayLocks } from "@frontend/Financial/TimesheetDayLocks/useDayLocks";
import { WEEKDAY_NAMES, formatMonthDay, weekdayOfDateKey } from "@frontend/Financial/TimesheetDayLocks/dayLockHelpers";
import "@frontend/Financial/TimesheetDayLocks/TimesheetDayLocks.css";
import "./LogBillableEntryModal.css";

type EntryType = "general" | "timeEntry" | "fixedFee";

interface LogBillableEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  matterId?: string;
  matterStatus?: string;
}

const LOCKED_STATUSES = ["complete", "on hold"];

function isMatterLocked(status: string | undefined): boolean {
  if (!status) return false;
  const cleaned = status.toLowerCase().trim().replace(/^status:\s*/i, "");
  return LOCKED_STATUSES.includes(cleaned);
}

function getNameFromEmployeeRow(e: any): string {
  if (e.FirstName && e.LastName) return `${e.FirstName} ${e.LastName}`;
  if (e.firstname && e.lastname) return `${e.firstname} ${e.lastname}`;
  if (e.FirstName) return e.FirstName;
  if (e.firstname) return e.firstname;
  return e.EmployeeID || e.employeeid || "Unknown";
}

const LogBillableEntryModal: React.FC<LogBillableEntryModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  matterId,
  matterStatus,
}) => {
  const employeeId = useActiveEmployeeId();
  const currentEmployee = useCurrentEmployee();
  const canLogTimeForOthers = (() => {
    const p = currentEmployee?.permish_id;
    const n = typeof p === "string" ? parseInt(p, 10) : p;
    return n === 1;
  })();
  const isTimesheetOnlyUser = (() => {
    const p = currentEmployee?.permish_id;
    const n = typeof p === "string" ? parseInt(p, 10) : p;
    return n === 2 || n === 3;
  })();
  const isLockedForUser = isTimesheetOnlyUser && isMatterLocked(matterStatus);

  const [entryType, setEntryType] = useState<EntryType>("general");
  const [timeEntrySubjectEmployeeId, setTimeEntrySubjectEmployeeId] = useState<string>("");
  const [employeePickerOptions, setEmployeePickerOptions] = useState<{ id: string; name: string }[]>([]);
  const [matter, setMatter] = useState<any>(null);
  const [employee, setEmployee] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Time Entry fields
  const [date, setDate] = useState(calgaryDateString());
  // Timesheet day locks (plans/todo/timesheet-day-locks.md, decision h): this form has no cost-code
  // field, so it cannot build a request. A permish 2/3 user picking a locked date for a time entry is
  // pointed at the timesheet cell instead, and submit is disabled (the server would refuse anyway).
  // Locks apply to everyone but permish 1 — including a loaded employee with NULL permish (decision b).
  const isDayLockRestricted = (() => {
    if (!currentEmployee) return false;
    const p = currentEmployee.permish_id;
    return (typeof p === "string" ? parseInt(p, 10) : p) !== 1;
  })();
  const dayLocks = useDayLocks(isFeatureEnabled("timesheetDayLocks") && isOpen && isDayLockRestricted);
  const lockedWeekday =
    entryType === "timeEntry" && dayLocks.isDayLocked(date) ? weekdayOfDateKey(date) : null;
  // A locked DATE names its lock (plans/todo/timesheet-date-locks.md, F2); a weekday keeps its sentence.
  const lockedDateLabel = lockedWeekday !== null ? dayLocks.lockLabelFor(date) : null;
  const [hours, setHours] = useState("");
  const [hourlyRate, setHourlyRate] = useState("");
  const [workDescription, setWorkDescription] = useState("");
  const [isBillable, setIsBillable] = useState(true);

  // Expense fields
  const [expenseDescription, setExpenseDescription] = useState("");
  const [expenseAmount, setExpenseAmount] = useState("");
  const [taxPercent, setTaxPercent] = useState("0");

  // Fixed Fee: search filter and create form
  const [feeSearch, setFeeSearch] = useState("");
  const [showCreateFee, setShowCreateFee] = useState(false);
  const [newFeeCode, setNewFeeCode] = useState("");
  const [newNarrative, setNewNarrative] = useState("");
  const [newFees, setNewFees] = useState("");

  // Fetch fee templates when Fixed Fee tab is active
  const {
    data: feeTemplates,
    isLoading: feesLoading,
    error: feesError,
    refetch: refetchFees,
  } = useQuery({
    queryKey: ["fixedFees"],
    queryFn: FinancialService.getFixedFees,
    enabled: isOpen && entryType === "fixedFee",
  });

  const rawFees = feeTemplates?.data ?? feeTemplates;
  const feeList = Array.isArray(rawFees) ? rawFees : [];
  const filteredFees = feeList.filter((t: any) => {
    if (!feeSearch) return true;
    const q = feeSearch.toLowerCase();
    return (
      t.fee_code?.toLowerCase().includes(q) ||
      t.narrative?.toLowerCase().includes(q)
    );
  });

  useEffect(() => {
    if (!isOpen) {
      setTimeEntrySubjectEmployeeId("");
      return;
    }
    if (canLogTimeForOthers && employeeId) {
      setTimeEntrySubjectEmployeeId(employeeId);
    }
  }, [isOpen, canLogTimeForOthers, employeeId]);

  useEffect(() => {
    if (!isOpen || !canLogTimeForOthers) {
      setEmployeePickerOptions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const raw = await EmployeeService.getEmployees();
        const list = Array.isArray(raw) ? raw : [];
        if (cancelled) return;
        const opts = list
          .map((e: any) => {
            const id = e.EmployeeID || e.employeeid;
            return id ? { id, name: getNameFromEmployeeRow(e) } : null;
          })
          .filter(Boolean) as { id: string; name: string }[];
        opts.sort((a, b) => a.name.localeCompare(b.name));
        setEmployeePickerOptions(opts);
      } catch (e) {
        console.error("Error loading employees for picker:", e);
        setEmployeePickerOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, canLogTimeForOthers]);

  // Fetch matter and employee data (time entry subject follows picker when supervisor)
  useEffect(() => {
    const fetchData = async () => {
      if (matterId) {
        try {
          const matterData = await MatterService.getMatterName(matterId);
          if (matterData) {
            setMatter({
              matterid: matterId,
              mattername: matterData.mattername || matterId,
            });
          }
        } catch (error) {
          console.error("Error fetching matter:", error);
        }
      }

      const subjectId =
        canLogTimeForOthers && timeEntrySubjectEmployeeId
          ? timeEntrySubjectEmployeeId
          : employeeId;
      if (subjectId) {
        try {
          const employeeData = await EmployeeService.getEmployeeById(subjectId);
          setEmployee(employeeData);
          if (employeeData?.billedrate) {
            setHourlyRate(employeeData.billedrate.toString());
          }
        } catch (error) {
          console.error("Error fetching employee:", error);
        }
      }
    };

    if (isOpen) {
      fetchData();
    }
  }, [employeeId, matterId, isOpen, canLogTimeForOthers, timeEntrySubjectEmployeeId]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setEntryType("general");
      setHours("");
      setHourlyRate(employee?.billedrate?.toString() || "");
      setWorkDescription("");
      setIsBillable(true);
      setExpenseDescription("");
      setExpenseAmount("");
      setTaxPercent("0");
      setDate(calgaryDateString());
      setFeeSearch("");
      setShowCreateFee(false);
      setNewFeeCode("");
      setNewNarrative("");
      setNewFees("");
      setError("");
    }
  }, [isOpen, employee]);

  const formatTimeRecorded = (time: string): string => {
    const totalMinutes = parseFloat(time) * 60;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.floor(totalMinutes % 60);
    return `${hours < 10 ? "0" + hours : hours}:${
      minutes < 10 ? "0" + minutes : minutes
    }:00`;
  };

  const handleCreateFixedFee = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await FinancialService.addFixedFee({
        fee_code: newFeeCode,
        narrative: newNarrative,
        fees: parseFloat(newFees),
      });
      setNewFeeCode("");
      setNewNarrative("");
      setNewFees("");
      setShowCreateFee(false);
      refetchFees();
    } catch (err: any) {
      console.error("Error creating fixed fee:", err);
      setError(err.message || "Failed to create fixed fee.");
    } finally {
      setLoading(false);
    }
  };

  const handleAssignFixedFee = async (template: any) => {
    if (isLockedForUser) {
      setError("This project is locked (complete or on hold). You cannot assign fees.");
      return;
    }
    if (!matterId || !matter) {
      setError("Matter information is missing");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await FinancialService.addAssignedFixedFees({
        fee_code: template.fee_code,
        narrative: template.narrative,
        fees: template.fees,
        matterid: matterId,
      });
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error("Error assigning fixed fee:", err);
      setError(err.message || "Failed to assign fixed fee.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (isLockedForUser) {
      setError("This project is locked (complete or on hold). You cannot log time.");
      return;
    }

    setLoading(true);

    try {
      if (!matterId || !matter) {
        setError("Matter information is missing");
        setLoading(false);
        return;
      }

      if (entryType === "timeEntry") {
        if (!employee) {
          setError("Employee data is missing");
          setLoading(false);
          return;
        }

        const formattedTime = formatTimeRecorded(hours);

        const subjectEmployeeId =
          canLogTimeForOthers && timeEntrySubjectEmployeeId
            ? timeEntrySubjectEmployeeId
            : employee?.employeeid;
        if (!subjectEmployeeId) {
          setError("Employee is required");
          setLoading(false);
          return;
        }

        const formData = {
          time_recorded: formattedTime,
          date_recorded: date,
          notes: workDescription,
          employeeid: subjectEmployeeId,
          matterid: matterId,
          isBillable, // A3: carry the "billable to client" flag (same fix as the Financial/Billing modal)
        };

        await FinancialService.logTime(formData);
      } else if (entryType === "general") {
        if (!expenseDescription || !expenseAmount || !employeeId) {
          setError("Please fill in all required fields");
          setLoading(false);
          return;
        }

        const formData = {
          description: expenseDescription,
          amount: parseFloat(expenseAmount),
          date: date,
          employeeid: employeeId,
          matterid: matterId,
          tax_percent: parseFloat(taxPercent) || 0,
        };

        await FinancialService.addOtherBilling(formData);
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error("Error adding billable entry:", error);
      setError(error.message || "Failed to add billable entry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Log Billable Entry"
      size="medium"
    >
      {/* Entry Type Selection */}
      <div className="log-billable-entry-type-tabs">
        <button
          className={`log-billable-entry-tab ${
            entryType === "general" ? "active" : ""
          }`}
          onClick={() => setEntryType("general")}
        >
          General
        </button>
        <button
          className={`log-billable-entry-tab ${
            entryType === "timeEntry" ? "active" : ""
          }`}
          onClick={() => setEntryType("timeEntry")}
        >
          Time Entry
        </button>
        <button
          className={`log-billable-entry-tab ${
            entryType === "fixedFee" ? "active" : ""
          }`}
          onClick={() => setEntryType("fixedFee")}
        >
          Fixed Fee
        </button>
      </div>

      {isLockedForUser && (
        <div className="log-billable-entry-error" style={{ background: '#fff3cd', color: '#856404', borderColor: '#ffc107' }}>
          This project is {matterStatus?.toLowerCase().includes('complete') ? 'complete' : 'on hold'}. Time entry is locked for your role.
        </div>
      )}
      {error && <div className="log-billable-entry-error">{error}</div>}

      {/* Fixed Fee tab: clickable catalog list */}
      {entryType === "fixedFee" && (
        <div className="log-billable-fixed-fee-section">
          <div className="log-billable-entry-field">
            <label>Matter</label>
            <input
              type="text"
              value={matter ? `${matter.matterid}: ${matter.mattername || ""}` : "Loading..."}
              disabled
              className="log-billable-entry-disabled-input"
            />
          </div>

          <div className="log-billable-entry-field">
            <label>Search Fees</label>
            <input
              type="text"
              value={feeSearch}
              onChange={(e) => setFeeSearch(e.target.value)}
              placeholder="Filter by code or narrative..."
            />
          </div>

          <div className="log-billable-fee-create-toggle">
            <button
              type="button"
              className="log-billable-fee-create-btn"
              onClick={() => setShowCreateFee(!showCreateFee)}
            >
              {showCreateFee ? "Cancel" : "Create New Fixed Fee"}
            </button>
          </div>

          {showCreateFee && (
            <form
              className="log-billable-fee-create-form"
              onSubmit={handleCreateFixedFee}
            >
              <div className="log-billable-entry-field">
                <label>Fee Code</label>
                <input
                  type="text"
                  value={newFeeCode}
                  onChange={(e) => setNewFeeCode(e.target.value)}
                  placeholder="e.g. CONV-01"
                  required
                />
              </div>
              <div className="log-billable-entry-field">
                <label>Narrative</label>
                <input
                  type="text"
                  value={newNarrative}
                  onChange={(e) => setNewNarrative(e.target.value)}
                  placeholder="e.g. Standard Conveyancing Fee"
                  required
                />
              </div>
              <div className="log-billable-entry-field">
                <label>Amount</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newFees}
                  onChange={(e) => setNewFees(e.target.value)}
                  placeholder="0.00"
                  required
                />
              </div>
              <button
                type="submit"
                className="log-billable-entry-save"
                disabled={loading}
              >
                <FontAwesomeIcon icon={faCheck} />
                Save Fixed Fee
              </button>
            </form>
          )}

          {feesLoading && (
            <p className="log-billable-entry-hint">Loading fixed fees...</p>
          )}
          {feesError && (
            <div className="log-billable-entry-error">Error loading fixed fees.</div>
          )}

          <div className="log-billable-fee-list">
            {filteredFees.length === 0 && !feesLoading && (
              <p className="log-billable-fee-empty">No fixed fees found. Create one above to get started.</p>
            )}
            {filteredFees.map((template: any) => (
              <div
                key={template.fee_code}
                className="log-billable-fee-item"
              >
                <div className="log-billable-fee-item-info">
                  <span className="log-billable-fee-code">{template.fee_code}</span>
                  <span className="log-billable-fee-narrative">{template.narrative}</span>
                </div>
                <div className="log-billable-fee-item-right">
                  <span className="log-billable-fee-amount">${template.fees}</span>
                  <button
                    type="button"
                    className="log-billable-fee-assign-btn"
                    disabled={loading}
                    onClick={() => handleAssignFixedFee(template)}
                    title="Assign to this matter"
                  >
                    <FontAwesomeIcon icon={faPlus} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Form for General and Time Entry tabs */}
      {entryType !== "fixedFee" && (
        <form onSubmit={handleSubmit} className="log-billable-entry-form">
          {/* Common Fields */}
          <div className="log-billable-entry-field">
            <label>Date</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </div>
          {lockedWeekday !== null && (
            <p className="tdl-modal-locked-note" role="status">
              {lockedDateLabel !== null ? (
                <>
                  {formatMonthDay(date)} is locked ({lockedDateLabel}). Click that cell on the timesheet to request time.
                </>
              ) : (
                <>
                  {WEEKDAY_NAMES[lockedWeekday]}s are locked. Click the {WEEKDAY_NAMES[lockedWeekday]} cell on the timesheet
                  to request time.
                </>
              )}
            </p>
          )}

          <div className="log-billable-entry-field">
            <label>Matter</label>
            <input
              type="text"
              value={matter ? `${matter.matterid}: ${matter.mattername || ""}` : "Loading..."}
              disabled
              className="log-billable-entry-disabled-input"
            />
            <p className="log-billable-entry-hint">
              Matter is pre-selected based on current context.
            </p>
          </div>

          {/* Time Entry Fields */}
          {entryType === "timeEntry" && (
            <>
              {canLogTimeForOthers && (
                <div className="log-billable-entry-field">
                  <label>Employee</label>
                  <select
                    value={timeEntrySubjectEmployeeId || employeeId || ""}
                    onChange={(e) => setTimeEntrySubjectEmployeeId(e.target.value)}
                    className="log-billable-entry-select"
                    required
                  >
                    {employeePickerOptions.map((opt) => (
                      <option key={opt.id} value={opt.id}>
                        {opt.name}
                      </option>
                    ))}
                  </select>
                  <p className="log-billable-entry-hint">
                    Log time on behalf of the selected employee. Rates follow their profile.
                  </p>
                </div>
              )}
              <div className="log-billable-entry-field">
                <label>Hours</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                  required
                />
              </div>

              <div className="log-billable-entry-field">
                <label>Hourly Rate</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={hourlyRate}
                  disabled
                  className="log-billable-entry-disabled-input"
                />
                <p className="log-billable-entry-hint">
                  Rate is resolved from your profile. All three rate amounts
                  (billed, cost of work, field rate) are calculated automatically.
                </p>
              </div>

              <div className="log-billable-entry-field">
                <label>Work Description</label>
                <FormEntryMultilineTextbox
                  name="workDescription"
                  value={workDescription}
                  onChange={(e) => setWorkDescription(e.target.value)}
                  placeholder="Describe the work performed..."
                  rows={4}
                />
              </div>

              <div className="log-billable-entry-checkbox">
                <input
                  type="checkbox"
                  id="billable"
                  checked={isBillable}
                  onChange={(e) => setIsBillable(e.target.checked)}
                />
                <label htmlFor="billable">
                  This time is billable to client
                </label>
              </div>
            </>
          )}

          {/* General Fields */}
          {entryType === "general" && (
            <>
              <div className="log-billable-entry-field">
                <label>Description</label>
                <input
                  type="text"
                  value={expenseDescription}
                  onChange={(e) => setExpenseDescription(e.target.value)}
                  placeholder="Enter expense description"
                  required
                />
              </div>

              <div className="log-billable-entry-field">
                <label>Amount</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={expenseAmount}
                  onChange={(e) => setExpenseAmount(e.target.value)}
                  placeholder="0.00"
                  required
                />
              </div>

              <div className="log-billable-entry-field">
                <label>Tax Percent (Optional)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={taxPercent}
                  onChange={(e) => setTaxPercent(e.target.value)}
                  placeholder="0"
                />
              </div>
            </>
          )}

          {/* Action Buttons */}
          <div className="log-billable-entry-actions">
            <button
              type="button"
              className="log-billable-entry-cancel"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="log-billable-entry-save"
              disabled={loading || isLockedForUser || lockedWeekday !== null}
            >
              <FontAwesomeIcon icon={faCheck} />
              Save to Queue
            </button>
          </div>
        </form>
      )}
    </FormModal>
  );
};

export default LogBillableEntryModal;
